/* import __COLOCATED_TEMPLATE__ from './report-schedules.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type AppService from 'embercom/services/app-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type NotificationsService from 'embercom/services/notifications-service';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import ajax, { get } from 'embercom/lib/ajax';
import {
  getRolesData,
  getUsersData,
  getEmailsData,
} from 'embercom/utils/recipient-resolution-helpers';

interface Args {
  reportId?: number;
  onEditSchedule?: (schedule: ScheduledExternalShare) => void;
  onScheduleDeleted?: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

interface SchedulesApiResponse {
  data: Array<Record<string, any>>;
  total_hits: number;
}

export default class ReportSchedules extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare appService: AppService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked schedules: ScheduledExternalShare[] = [];
  @tracked isLoading = true;
  @tracked hasError = false;

  get isStandaloneApp(): boolean {
    return this.appService.app?.isStandaloneApp ?? false;
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (args.reportId) {
      taskFor(this.loadSchedulesTask).perform();
    }
  }

  get hasSchedules(): boolean {
    return this.schedules.length > 0;
  }

  get tableColumns() {
    if (this.isStandaloneApp) {
      return [
        {
          label: this.intl.t('reporting.manage-schedules.table.name'),
          valuePath: 'name',
          width: '50%',
        },
        {
          label: this.intl.t('reporting.manage-schedules.table.recipients'),
          valuePath: 'recipients',
          width: '40%',
        },
        {
          label: '',
          valuePath: 'actions',
          width: '10%',
        },
      ];
    }

    return [
      {
        label: this.intl.t('reporting.manage-schedules.table.name'),
        valuePath: 'name',
        width: '55%',
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.recipients'),
        valuePath: 'recipients',
        width: '45%',
      },
    ];
  }

  @task({ drop: true })
  *loadSchedulesTask(): TaskGenerator<void> {
    if (!this.args.reportId) {
      this.isLoading = false;
      return;
    }

    try {
      let response: SchedulesApiResponse = yield get('/ember/reporting/scheduled_external_share', {
        app_id: this.appService.app.id,
        report_id: this.args.reportId,
      });

      this.schedules = this.generateScheduleModels(response.data || []);
      this.hasError = false;
    } catch {
      this.hasError = true;
      this.schedules = [];
    } finally {
      this.isLoading = false;
    }
  }

  private generateScheduleModels(
    objects: Array<Record<string, any>> = [],
  ): ScheduledExternalShare[] {
    return objects.map((schedule) => {
      let normalizedObject = this.store.normalize('reporting/scheduled-external-share', schedule);
      return this.store.push(normalizedObject) as ScheduledExternalShare;
    });
  }

  @action
  recipientsDataForSchedule(schedule: ScheduledExternalShare) {
    return {
      roles: getRolesData(this.store, this.intl, schedule),
      users: getUsersData(this.store, this.intl, schedule),
      emails: getEmailsData(this.intl, schedule),
    };
  }

  @cached
  get actionMenuItemsForSchedule() {
    return (schedule: ScheduledExternalShare) => {
      return [
        {
          items: [
            {
              text: this.intl.t('reporting.schedule-delivery.report-schedules.actions.edit'),
              icon: 'edit',
              onSelectItem: () => this.editSchedule(schedule),
            },
            {
              text: this.intl.t('reporting.schedule-delivery.report-schedules.actions.delete'),
              icon: 'trash',
              onSelectItem: () => this.deleteSchedule(schedule.id),
              isDestructive: true,
            },
          ],
        },
      ];
    };
  }

  @action
  editSchedule(schedule: ScheduledExternalShare) {
    if (this.args.onEditSchedule) {
      this.args.onEditSchedule(schedule);
    }
  }

  @action
  async deleteSchedule(scheduleId: string) {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('reporting.schedule-delivery.report-schedules.delete-confirm.title'),
      body: this.intl.t('reporting.schedule-delivery.report-schedules.delete-confirm.body'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t(
        'reporting.schedule-delivery.report-schedules.delete-confirm.confirm-button',
      ),
    });

    if (!confirmed) {
      return;
    }

    try {
      let url = `/ember/reporting/scheduled_external_share/${scheduleId}?app_id=${this.appService.app.id}`;
      await ajax({
        url,
        type: 'DELETE',
        dataType: 'text',
      });

      this.schedules = this.schedules.filter((s) => String(s.id) !== String(scheduleId));
      this.notificationsService.notifyConfirmation(
        this.intl.t('reporting.schedule-delivery.report-schedules.delete-success'),
      );
      this.args.onScheduleDeleted?.();
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('reporting.schedule-delivery.report-schedules.delete-error'),
      );
    }
  }

  @action
  navigateToManageSchedules() {
    this.router.transitionTo('apps.app.reports.manage-schedules', {
      queryParams: { reportId: this.args.reportId },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ScheduleDeliveryDrawer::ReportSchedules': typeof ReportSchedules;
    'reporting/custom/report/schedule-delivery-drawer/report-schedules': typeof ReportSchedules;
  }
}
